/**
 * Template Context - Manages device templates from API
 * 
 * AI Agent Note: This context fetches and caches device templates from the backend.
 * Templates are the library of reusable device specifications.
 * Separate from DeviceContext which manages instances in the current rig.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { DeviceTemplate } from '../types';
import { api } from '../services/api';

interface TemplateContextType {
  templates: DeviceTemplate[];
  loading: boolean;
  error: string | null;
  refreshTemplates: () => Promise<void>;
  getTemplateById: (id: string) => DeviceTemplate | undefined;
  createTemplate: (template: Omit<DeviceTemplate, 'id' | 'createdAt' | 'updatedAt' | 'isBuiltIn'>) => Promise<DeviceTemplate>;
  deleteTemplate: (id: string) => Promise<void>;
}

const TemplateContext = createContext<TemplateContextType | undefined>(undefined);

export const useTemplates = (): TemplateContextType => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplates must be used within a TemplateProvider');
  }
  return context;
};

interface TemplateProviderProps {
  children: ReactNode;
}

export const TemplateProvider: React.FC<TemplateProviderProps> = ({ children }) => {
  const [templates, setTemplates] = useState<DeviceTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await api.templates.getAll();
      setTemplates(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load templates';
      setError(message);
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getTemplateById = useCallback((id: string): DeviceTemplate | undefined => {
    return templates.find(t => t.id === id);
  }, [templates]);

  const createTemplate = useCallback(async (templateData: Omit<DeviceTemplate, 'id' | 'createdAt' | 'updatedAt' | 'isBuiltIn'>): Promise<DeviceTemplate> => {
    const newTemplate = await api.templates.create({ ...templateData, isBuiltIn: false });
    setTemplates(prev => [...prev, newTemplate]);
    return newTemplate;
  }, []);

  const deleteTemplate = useCallback(async (id: string): Promise<void> => {
    await api.templates.delete(id);
    setTemplates(prev => prev.filter(t => t.id !== id));
  }, []);

  // Load templates on mount
  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  const value: TemplateContextType = {
    templates,
    loading,
    error,
    refreshTemplates,
    getTemplateById,
    createTemplate,
    deleteTemplate,
  };

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
};
